import { Controller } from '@hotwired/stimulus'
import { Modal } from 'flowbite'
import Camera from './utils/camera'
import type { ModalOptions, ModalInterface } from 'flowbite'
import { createElement, Minus } from 'lucide'
import { DirectUpload } from '@rails/activestorage'
import Stopwatch from './utils/stopwatch'

interface UploadedFile {
  signed_id: string
  filename: string
}

// Connects to data-controller="checklist"
export default class extends Controller {
  static targets = [
    'modal',
    'video',
    'alert',
    'takePhoto',
    'takeVideo',
    'mediaContainer',
    'stopVideo',
    'form',
    'recordingDuration',
    'progressContainer',
    'submitButton',
    'backButton',
    'openCameraButton',
  ]
  declare readonly modalTarget: HTMLElement
  declare readonly videoTarget: HTMLVideoElement
  declare readonly alertTarget: HTMLElement
  declare readonly takePhotoTarget: HTMLButtonElement
  declare readonly takeVideoTarget: HTMLButtonElement
  declare readonly stopVideoTarget: HTMLButtonElement
  declare readonly mediaContainerTarget: HTMLDivElement
  declare readonly formTarget: HTMLFormElement
  declare readonly recordingDurationTarget: HTMLDivElement
  declare readonly progressContainerTargets: HTMLDivElement[]
  declare readonly submitButtonTarget: HTMLButtonElement
  declare readonly backButtonTarget: HTMLAnchorElement
  declare readonly openCameraButtonTarget: HTMLButtonElement

  private readonly modalOptions: ModalOptions = {
    placement: 'center',
    backdrop: 'dynamic',
    backdropClasses: 'bg-gray-900/50 dark:bg-gray-900/80 fixed inset-0 z-90',
    closable: true,
    onHide: () => {
      console.log('Modal hidden')
      this.#stopCamera()
      this.#hideAlert()
    },
    onShow: () => {
      console.log('Modal shown')
      this.#startCamera()
    },
  }
  private alert: string | null = null
  private modal: ModalInterface | null = null
  private camera: Camera | null = null
  private files: { id: string; blob: Blob }[] = []
  private uploadedFileIds: string[] = []
  private stopwatch: Stopwatch | null = null

  connect() {
    this.modal = new Modal(this.modalTarget, this.modalOptions)
    this.camera = new Camera(this.videoTarget)
    this.stopwatch = new Stopwatch(
      (time) => {
        this.recordingDurationTarget.innerText = time
      },
      () => {
        this.recordingDurationTarget.innerText = '00:00'
      }
    )
  }

  disconnect() {
    this.closeModal()
  }

  openModal() {
    this.modal?.show()
  }

  closeModal() {
    this.modal?.hide()
  }

  async submitForm(e: SubmitEvent) {
    e.preventDefault()
    if (!confirm('Apakah anda yakin?')) {
      return
    }
    console.log('submiting form')

    try {
      this.#toggleBtn(false)
      if (this.files.length > 0) {
        this.progressContainerTargets.forEach((element) => {
          element.classList.remove('hidden')
        })
        await this.#uploadAllFiles()
      }

      this.uploadedFileIds.forEach((id) => {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = 'user_checklist[medias][]'
        input.value = id
        this.formTarget.appendChild(input)
      })

      this.formTarget.submit()
      this.#toggleBtn(true)
    } catch (error) {
      console.error(error)
    }
  }

  deleteFile(e: PointerEvent & { params: Record<string, string> }): void {
    e.preventDefault()
    const params = e.params
    if (!params.id) {
      return
    }

    const indexToBeDeleted = this.files.findIndex(
      (file) => file.id === params.id
    )
    this.files.splice(indexToBeDeleted, 1)
    this.#removePreview(params.id)
  }

  logFiles(): void {
    console.log(this.files)
  }

  async capturePhoto() {
    if (!this.camera) {
      return
    }

    try {
      const blob = await this.camera.capturePhoto()
      this.#addFile(blob)
      this.modal?.hide()
    } catch (error) {
      this.#showAlert((error as Error).message)
      console.error('Failed to capture photo:', error)
    }
  }

  startRecording(): void {
    if (!this.camera) {
      return
    }

    try {
      this.camera.startRecording()
      this.#toggleStopVideoBtn(true)
      this.stopwatch?.start()
    } catch (error) {
      this.#showAlert('Failed to start recording')
      console.error(error)
    }
  }

  async stopRecording() {
    if (!this.camera) {
      return
    }

    try {
      const blob = await this.camera.stopRecording()
      this.stopwatch?.stop()
      this.#addFile(blob)
      this.#toggleStopVideoBtn(false)
      this.modal?.hide()
      console.log('recording stoped')
    } catch (error) {
      console.error(error)
    }
  }

  async #startCamera() {
    if (!this.camera) {
      return
    }

    try {
      await this.camera.start()
      this.#toggleCapturePhotoBtn(true)
      console.log('Camera started')
    } catch (error) {
      this.#showAlert((error as Error).message)
      console.error(error)
    }
  }

  #stopCamera() {
    if (this.camera) {
      this.camera.stop()
      this.#toggleCapturePhotoBtn(false)
      console.log('Camera stoped')
    }
  }

  #showAlert(msg: string) {
    this.alertTarget.innerText = msg
    this.alertTarget.classList.remove('hidden')
  }

  #hideAlert() {
    this.alertTarget.innerText = ''
    this.alertTarget.classList.add('hidden')
  }

  #toggleCapturePhotoBtn(status: boolean): void {
    this.takePhotoTarget.disabled = status ? false : true
    this.takeVideoTarget.disabled = status ? false : true

    if (status) {
      this.takePhotoTarget.classList.remove('text-neutral-700')
      this.takeVideoTarget.classList.remove('text-neutral-700')
    } else {
      this.takePhotoTarget.classList.add('text-neutral-700')
      this.takeVideoTarget.classList.add('text-neutral-700')
    }
  }

  #blobType(blob: Blob) {
    const type = blob.type
    console.log('Blob MIME type is: ' + type)

    if (type.startsWith('image/')) {
      return 'image'
    } else if (type.startsWith('video/')) {
      return 'video'
    } else {
      return 'unknown'
    }
  }

  #addFile(blob: Blob): void {
    const id = crypto.randomUUID()
    this.files.push({ id, blob })
    this.#createPreview(id, blob)
  }

  #createPreview(id: string, blob: Blob) {
    const div = document.createElement('div')
    div.id = id
    div.classList.add('relative')

    const btn = document.createElement('button')
    btn.classList.add('absolute', 'right-0', 'mx-4', 'my-2', 'z-10')
    btn.dataset.action = 'click->checklist#deleteFile'
    btn.dataset.checklistIdParam = id

    const minusIcon = createElement(Minus, {
      class: 'text-neutral-50',
    })
    btn.appendChild(minusIcon)
    div.appendChild(btn)

    if (this.#blobType(blob) === 'image') {
      const img = document.createElement('img')
      img.classList.add('h-auto', 'w-full', 'rounded-base')
      img.src = URL.createObjectURL(blob)
      div.appendChild(img)
    }

    if (this.#blobType(blob) === 'video') {
      const video = document.createElement('video')
      video.classList.add('h-auto', 'w-full', 'rounded-base')
      video.src = URL.createObjectURL(blob)
      video.controls = true
      div.appendChild(video)
    }

    const progressContainer = document.createElement('div')
    progressContainer.dataset.checklistTarget = 'progressContainer'
    progressContainer.classList.add(
      'absolute',
      'top-0',
      'flex',
      'justify-center',
      'items-center',
      'w-full',
      'h-full',
      'bg-neutral-100/70',
      'z-15',
      'hidden'
    )
    const progress = document.createElement('span')
    progress.id = `progress-${id}`
    progress.innerText = '0%'
    progressContainer.appendChild(progress)
    div.appendChild(progressContainer)

    this.mediaContainerTarget.appendChild(div)
  }

  #removePreview(id: string) {
    const el = document.getElementById(id)
    el?.remove()
  }

  #toggleStopVideoBtn(status: boolean): void {
    this.takeVideoTarget.parentElement?.classList.remove(
      status ? 'flex' : 'hidden'
    )
    this.takeVideoTarget.parentElement?.classList.add(
      status ? 'hidden' : 'flex'
    )

    this.stopVideoTarget.parentElement?.classList.remove(
      status ? 'hidden' : 'flex'
    )
    this.stopVideoTarget.parentElement?.classList.add(
      status ? 'flex' : 'hidden'
    )
  }

  #toggleBtn(status: boolean): void {
    if (status) {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.classList.remove('opacity-50')
      this.submitButtonTarget.classList.add('hover:bg-success-strong')
      this.openCameraButtonTarget.disabled = false
      this.backButtonTarget.style.pointerEvents = ''
    } else {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.classList.add('opacity-50')
      this.submitButtonTarget.classList.remove('hover:bg-success-strong')
      this.openCameraButtonTarget.disabled = true
      this.backButtonTarget.style.pointerEvents = 'none'
    }
  }

  async #uploadAllFiles(): Promise<void> {
    this.uploadedFileIds = []
    const total = this.files.length

    for (let i = 0; i < total; i++) {
      const file = this.files[i]

      const uploadedFile = await this.#uploadSingleFile(file.blob, file.id)
      this.uploadedFileIds.push(uploadedFile.signed_id)
    }

    console.log('All file successfully uploaded')
  }

  #uploadSingleFile(blob: Blob, id: string): Promise<UploadedFile> {
    return new Promise((resolve, reject) => {
      const file = new File([blob], id)
      const progressEl = document.getElementById(
        `progress-${id}`
      ) as HTMLElement

      console.log(`Uploading: ${id}`)
      const upload = new DirectUpload(
        file,
        '/rails/active_storage/direct_uploads',
        {
          directUploadWillStoreFileWithXHR: (xhr) => {
            xhr.upload.addEventListener('progress', (event) => {
              const progress = Math.floor((event.loaded / event.total) * 100)
              console.log(`Uploading: ${id} - ${progress}%`)
              progressEl.innerText = `${progress}%`
            })
            xhr.upload.addEventListener('load', (event) => {
              console.log(`Uploaded: ${id}`)
            })
          },
        }
      )

      upload.create((error, blob) => {
        if (error) {
          reject(error)
        } else {
          resolve(blob as UploadedFile)
        }
      })
    })
  }
}
